import { useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router'

/**
 * Where a new route opens (UI-QA Q5).
 *
 * `BrowserRouter` keeps the document's scroll position across a client-side
 * navigation, so following "Connect your headband" from 205px down Record
 * opened Connect at 205px, with its heading under the phone's sticky header.
 *
 * The rule, keyed on the PATHNAME and nothing else:
 *
 *  - a PUSH or REPLACE to a new pathname opens at the top, or at its `#hash`
 *    target when it has one (the target's `scroll-margin-top`, set in
 *    `shell.css`, keeps it clear of the sticky header);
 *  - a POP (Back/Forward) is left alone, so the browser restores where the
 *    reader was;
 *  - a search-parameter change, a data update or a capture phase is not a new
 *    route and never moves the page.
 *
 * Focus: when the element that had it went away with the old route (the link
 * that was followed, say -- a PUSH), focus would fall to `<body>`; it is placed on
 * `#main` instead, the skip link's own target, so the next Tab starts in the
 * new page. Focus that is somewhere real is left there, which is what keeps the
 * drawer's return-to-trigger behaviour intact.
 */
export function RouteScroll() {
  const { pathname, hash } = useLocation()
  const type = useNavigationType()
  const last = useRef(pathname)

  useLayoutEffect(() => {
    if (last.current === pathname) return
    last.current = pathname
    if (type === 'POP') return
    const target = hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null
    if (target !== null) target.scrollIntoView({ block: 'start' })
    else window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    // Only a followed link (PUSH) hands focus to #main. A REPLACE is a redirect (`/today` ->
    // `/report` on arrival): taking focus there put the first Tab past the skip link (UI-QA Q10).
    const active = document.activeElement
    if (type === 'PUSH' && (active === null || active === document.body)) {
      document.getElementById('main')?.focus({ preventScroll: true })
    }
  }, [pathname, hash, type])

  return null
}
